/**
 * ⚠️ HARDCODED / TEMPORARY — ported from dane's etf_website on 2026-06-24.
 *
 * Source-of-truth: dane's backend seed command
 *   `etf_website/management/commands/seed_fixed_income_funds.py`
 * which seeds the 6 fixed-income (bond) funds' allocation rows
 * (credit / maturity / industry / geography) into the DB, served by the
 * dane API as `FundDetailFixedExtras.allocation_breakdown` and rendered by
 * `<FundAllocationBreakdown>` (the "Credit Quality / Maturity / Industry /
 * Geography" tabbed bond-information section).
 *
 * etfsite's public API does NOT yet serve these (the FI branch of
 * FundDetailPage hardcodes `fixedExt = null`), so the bond pages render only
 * empty states. This file restores the bond-information section by providing
 * the same hardcoded breakdown the dane API computes, keyed by ticker.
 *
 * De-hardcode later: once the etfsite API (apps/api) serves an
 * `allocation_breakdown` per FI fund (from admin DB / ingestion), replace
 * `getBondFixedExtras()` with the live payload and delete this file.
 *
 * Color assignment mirrors dane's serializer exactly: segments are colored by
 * their position within each tab using the 8-color palette
 * (`_ALLOC_SEGMENT_COLORS` in dane's `etf_website/services/__init__.py`),
 * cycling `palette[idx % 8]`.
 */

/** Matches the `FundDetailFixedExtras` shape consumed by FundDetailPage's
 *  fixed-income branch (`fixedExt?.allocation_breakdown`). Kept structurally
 *  identical so `getBondFixedExtras()` is assignable to that local type. */
export interface AllocSegment {
  label: string;
  pct: number;
  color: string;
}

export interface BondAllocationBreakdown {
  credit_quality?: AllocSegment[];
  maturity?: AllocSegment[];
  industry?: AllocSegment[];
  geography?: AllocSegment[];
  as_of_date?: string | null;
  weighted_avg_maturity?: string | null;
}

export interface BondFixedExtras {
  allocation_breakdown?: BondAllocationBreakdown | null;
  distribution_history?: null;
}

/** Positional segment palette — byte-identical to dane's `_ALLOC_SEGMENT_COLORS`. */
const ALLOC_SEGMENT_COLORS = [
  "#3B82F6", // blue-500
  "#10B981", // emerald-500
  "#F59E0B", // amber-500
  "#EF4444", // red-500
  "#8B5CF6", // violet-500
  "#06B6D4", // cyan-500
  "#EC4899", // pink-500
  "#6B7280", // gray-500
] as const;

/** [label, percentage] pairs in display order, as seeded in dane. */
type RawRow = readonly [string, number];

/** Map raw [label, pct] rows → colored segments (dane's `palette[idx % 8]`). */
function toSegments(rows: readonly RawRow[]): AllocSegment[] {
  return rows.map(([label, pct], idx) => ({
    label,
    pct,
    // idx % length is always in range; `?? gray-500` satisfies
    // noUncheckedIndexedAccess without a forbidden non-null assertion.
    color: ALLOC_SEGMENT_COLORS[idx % ALLOC_SEGMENT_COLORS.length] ?? "#6B7280",
  }));
}

interface BondFundRaw {
  /** Display order (from dane seed `display_order`). */
  order: number;
  /** Weighted-avg maturity string — surfaces in the Maturity tab subtitle. */
  weightedAvgMaturity: string;
  /** Effective duration string — informational; the sidebar Fund Facts row is
   *  built by the mapper from DDB `effectiveDuration`. Kept here as the dane
   *  canonical value for parity / future fallback. */
  effectiveDuration: string;
  indexName: string;
  indexProvider: string;
  rebalanceFrequency: string;
  distributionFrequency: string;
  /** Maturity-band label (e.g. "1-3 Years") — Maturity tab subtitle fallback. */
  maturityBandLabel: string;
  credit: readonly RawRow[];
  maturity: readonly RawRow[];
  industry: readonly RawRow[];
  geography: readonly RawRow[];
}

// ──────────────────────────────────────────────────────────────────────────
// Hardcoded per-fund allocation data — transcribed verbatim from
// seed_fixed_income_funds.py (FUND_RECORDS). Keyed by uppercase ticker.
// ──────────────────────────────────────────────────────────────────────────
const FI_SHARED = {
  indexProvider: "FTSE Fixed Income LLC",
  rebalanceFrequency: "Monthly",
  distributionFrequency: "Monthly",
} as const;

const BOND_FUND_RAW: Record<string, BondFundRaw> = {
  CGOV: {
    order: 100,
    effectiveDuration: "0.15 yrs",
    weightedAvgMaturity: "0.13 yrs",
    indexName: "FTSE US Treasury Bill Index 0-3 Month",
    ...FI_SHARED,
    maturityBandLabel: "0-3 Months",
    credit: [
      ["U.S. Treasury", 98.5],
      ["Cash & Equivalents", 1.5],
    ],
    maturity: [
      ["0-1 Month", 40.0],
      ["1-3 Months", 58.5],
      ["Cash", 1.5],
    ],
    industry: [],
    geography: [],
  },
  CBIL: {
    order: 110,
    effectiveDuration: "0.55 yrs",
    weightedAvgMaturity: "0.50 yrs",
    indexName: "FTSE US Treasury Bill 3-12 Month Index",
    ...FI_SHARED,
    maturityBandLabel: "3-12 Months",
    credit: [
      ["U.S. Treasury", 98.5],
      ["Cash & Equivalents", 1.5],
    ],
    maturity: [
      ["3-6 Months", 35.0],
      ["6-12 Months", 63.5],
      ["Cash", 1.5],
    ],
    industry: [],
    geography: [],
  },
  CUST: {
    order: 120,
    effectiveDuration: "1.85 yrs",
    weightedAvgMaturity: "1.90 yrs",
    indexName: "FTSE US Treasury Index 1-3 Year",
    ...FI_SHARED,
    maturityBandLabel: "1-3 Years",
    credit: [
      ["U.S. Treasury", 98.5],
      ["Cash & Equivalents", 1.5],
    ],
    maturity: [
      ["1-2 Years", 55.0],
      ["2-3 Years", 43.5],
      ["Cash", 1.5],
    ],
    industry: [],
    geography: [],
  },
  CIEI: {
    order: 130,
    effectiveDuration: "4.50 yrs",
    weightedAvgMaturity: "4.80 yrs",
    indexName: "FTSE US Treasury Index 3-7 Year",
    ...FI_SHARED,
    maturityBandLabel: "3-7 Years",
    credit: [
      ["U.S. Treasury", 98.5],
      ["Cash & Equivalents", 1.5],
    ],
    maturity: [
      ["3-4 Years", 28.0],
      ["4-5 Years", 26.0],
      ["5-6 Years", 23.0],
      ["6-7 Years", 21.5],
      ["Cash", 1.5],
    ],
    industry: [],
    geography: [],
  },
  CIVG: {
    order: 140,
    effectiveDuration: "2.65 yrs",
    weightedAvgMaturity: "2.80 yrs",
    indexName: "FTSE US Broad Investment-Grade Corporate Bond Index 1-5 Year",
    ...FI_SHARED,
    maturityBandLabel: "1-5 Years",
    credit: [
      ["AAA", 3.0],
      ["AA+", 4.5],
      ["AA", 5.0],
      ["AA-", 6.0],
      ["A+", 12.0],
      ["A", 18.5],
      ["A-", 14.5],
      ["BBB+", 13.0],
      ["BBB", 12.0],
      ["BBB-", 10.0],
      ["Cash & Equivalents", 1.5],
    ],
    maturity: [
      ["1-2 Years", 38.0],
      ["2-3 Years", 32.0],
      ["3-4 Years", 17.0],
      ["4-5 Years", 11.5],
      ["Cash", 1.5],
    ],
    industry: [
      ["Financials", 42.0],
      ["Industrials", 28.5],
      ["Consumer", 12.5],
      ["Communications & Tech", 8.5],
      ["Utilities", 5.5],
      ["Energy", 3.0],
    ],
    geography: [
      ["United States", 84.5],
      ["United Kingdom", 4.0],
      ["Canada", 3.5],
      ["Other Europe", 3.5],
      ["Japan", 1.5],
      ["Australia", 1.0],
      ["Other", 0.5],
      ["Cash", 1.5],
    ],
  },
  CHYG: {
    order: 150,
    effectiveDuration: "2.40 yrs",
    weightedAvgMaturity: "2.85 yrs",
    indexName: "FTSE US High-Yield Market 0-5 Years 2% Capped Index",
    ...FI_SHARED,
    maturityBandLabel: "0-5 Years",
    credit: [
      ["BB+", 18.5],
      ["BB", 19.0],
      ["BB-", 14.5],
      ["B+", 13.0],
      ["B", 12.5],
      ["B-", 9.5],
      ["CCC+", 5.5],
      ["CCC", 3.0],
      ["CCC-", 1.0],
      ["Not Rated", 2.0],
      ["Cash & Equivalents", 1.5],
    ],
    maturity: [
      ["0-1 Year", 12.0],
      ["1-2 Years", 22.0],
      ["2-3 Years", 26.0],
      ["3-4 Years", 21.0],
      ["4-5 Years", 17.5],
      ["Cash", 1.5],
    ],
    industry: [
      ["Services", 32.0],
      ["Industrials", 28.5],
      ["Consumer", 14.5],
      ["Energy", 9.0],
      ["Communications & Tech", 7.5],
      ["Financials", 6.0],
      ["Utilities", 2.5],
    ],
    geography: [
      ["United States", 86.0],
      ["Canada", 3.5],
      ["United Kingdom", 2.5],
      ["Other Europe", 3.0],
      ["Bermuda/Caribbean", 2.0],
      ["Other", 1.5],
      ["Cash", 1.5],
    ],
  },
};

/**
 * Hardcoded fixed-income extras for a bond fund, keyed by ticker (case-
 * insensitive). Returns the `allocation_breakdown` shape consumed by
 * FundDetailPage's FI branch, or `null` for non-bond / unknown tickers
 * (so the existing empty-state path is preserved).
 *
 * Empty tabs (industry / geography on the Treasury funds) are omitted —
 * `FundAllocationBreakdown` only renders tabs whose segment array is non-empty.
 */
export function getBondFixedExtras(ticker: string | null | undefined): BondFixedExtras | null {
  const raw = ticker ? BOND_FUND_RAW[ticker.toUpperCase()] : undefined;
  if (!raw) return null;

  const breakdown: BondAllocationBreakdown = {
    as_of_date: null,
    // Surfaces in the Maturity tab as "Weighted Avg Maturity · X.XX yrs".
    weighted_avg_maturity: raw.weightedAvgMaturity,
  };
  if (raw.credit.length) breakdown.credit_quality = toSegments(raw.credit);
  if (raw.maturity.length) breakdown.maturity = toSegments(raw.maturity);
  if (raw.industry.length) breakdown.industry = toSegments(raw.industry);
  if (raw.geography.length) breakdown.geography = toSegments(raw.geography);

  return { allocation_breakdown: breakdown, distribution_history: null };
}

/** True when the ticker is one of the 6 hardcoded fixed-income funds. */
export function isBondFund(ticker: string | null | undefined): boolean {
  return Boolean(ticker && BOND_FUND_RAW[ticker.toUpperCase()]);
}

/**
 * Per-fund index / duration / maturity metadata (dane canonical values),
 * exposed for optional mapper fallback when the DDB record lacks
 * `effectiveDuration` / `weightedAvgMaturity` / `indexName` / etc.
 * Not consumed by FundDetailPage today; kept colocated with the allocation
 * data so all hardcoded bond facts live in one de-hardcode-later file.
 */
export interface BondFundMeta {
  effectiveDuration: string;
  weightedAvgMaturity: string;
  indexName: string;
  indexProvider: string;
  rebalanceFrequency: string;
  distributionFrequency: string;
  maturityBandLabel: string;
}

export function getBondFundMeta(ticker: string | null | undefined): BondFundMeta | null {
  const raw = ticker ? BOND_FUND_RAW[ticker.toUpperCase()] : undefined;
  if (!raw) return null;
  return {
    effectiveDuration: raw.effectiveDuration,
    weightedAvgMaturity: raw.weightedAvgMaturity,
    indexName: raw.indexName,
    indexProvider: raw.indexProvider,
    rebalanceFrequency: raw.rebalanceFrequency,
    distributionFrequency: raw.distributionFrequency,
    maturityBandLabel: raw.maturityBandLabel,
  };
}
